import Echo from 'laravel-echo'
import Pusher from 'pusher-js'
import { sdk } from '@/lib/sdk'

const isDebug = import.meta.env.DEV || import.meta.env.MODE !== 'production'

function log(...args: any[]) {
    if (isDebug) {
        console.log('[Echo]', ...args)
    }
}

declare global {
    interface Window {
        Pusher: typeof Pusher
        Echo: Echo<'pusher'>
    }
}

/**
 * EchoService - Singleton service for Laravel Echo WebSocket connections
 *
 * Provides real-time broadcasting support for both public and private channels.
 * Private channels use the SDK's broadcast method for authentication.
 *
 * @example
 * ```typescript
 * // Initialize in main.ts
 * import EchoService from '@/services/EchoService'
 * EchoService.init()
 *
 * // Use in components via composables
 * import { useEchoChannel } from '@/composables/useEchoChannel'
 * import { usePrivateChannel } from '@/composables/usePrivateChannel'
 * ```
 */
class EchoService {
    private static instance: Echo<'pusher'> | null = null

    /**
     * Initialize Laravel Echo with Pusher broadcaster
     * Sets up authentication for private channels using the SDK
     */
    static init(): Echo<'pusher'> | null {
        if (this.instance) {
            return this.instance
        }

        const wsKey = import.meta.env.VITE_WEB_SOCKET_KEY ?? undefined
        const wsHost = import.meta.env.VITE_WEB_SOCKET_HOST ?? undefined
        const wsCluster = import.meta.env.VITE_PUSHER_APP_CLUSTER ?? ''

        log('🔌 Initializing Echo...', { wsHost, wsKey: wsKey ? '***' : null, cluster: wsCluster })

        window.Pusher = Pusher

        this.instance = new Echo({
            broadcaster: 'pusher',
            key: wsKey,
            wsHost: wsHost,
            cluster: wsCluster,
            forceTLS: false,
            encrypted: true,
            disableStats: true,
            enabledTransports: ['ws', 'wss'],
            authorizer: (channel: { name: string }) => {
                return {
                    authorize: (socketId: string, callback: (error: Error | null, data?: any) => void) => {
                        log('🔐 Authorizing channel:', channel.name)
                        sdk.auth.broadcast({
                            socket_id: socketId,
                            channel_name: channel.name
                        })
                            .then((response: any) => {
                                log('✅ Channel authorized:', channel.name)
                                callback(null, response.data)
                            })
                            .catch((error: any) => {
                                log('❌ Channel authorization failed:', channel.name, error)
                                callback(error instanceof Error ? error : new Error(String(error)), null)
                            })
                    }
                }
            }
        })

        window.Echo = this.instance
        log('✅ Echo initialized successfully')

        return this.instance
    }

    /**
     * Get the Echo instance
     */
    static getInstance(): Echo<'pusher'> | null {
        return this.instance
    }

    /**
     * Disconnect Echo and clean up
     */
    static disconnect(): void {
        if (this.instance) {
            this.instance.disconnect()
            this.instance = null
            delete (window as any).Echo
        }
    }
}

export default EchoService
